import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RubricBreakdown from "./RubricBreakdown";
import { useAppSelector } from "@store/hooks";
import {
  selectCanvasGrade,
  selectGradeDimensionWeights,
  selectGradingViewUserId,
  selectGradingViewUsername,
  selectIsGradingViewActive,
  selectReadingGrade,
  selectSelectedCanvasId,
  selectSelectedReadingId,
} from "@store/selectors";
import { useNotification } from "@shared/contexts/NotificationContext";
import {
  getStudentGradebook,
  releaseAllForStudent,
  setResourceWeight,
  setStudentOverride,
  setStudentGrade,
} from "@services/api";
import { computeWorkspaceTotal } from "@features/workspaces/utils/gradebook";
import type { RootState } from "@store/index";
import type { GradebookResourceRow } from "@/types/models";

type Row = GradebookResourceRow & { saving?: boolean };

const TYPE_LABEL: Record<string, string> = {
  reading: "Readings",
  canvas: "Canvases",
  paper: "Papers",
};
const TYPE_ORDER = ["reading", "canvas", "paper"] as const;

const clampInt = (v: string): number => Math.max(0, Math.floor(Number(v) || 0));

// Owner-side weighted rubric score (0–100) — mirrors the old GradeSection
// cards. Used to suggest a grade for the resource currently open in the
// pane (the only one with live rubric data).
const weightedScore = (
  dims: { enabled: boolean; score: number | null }[],
  ws: number[]
): number | null => {
  let wSum = 0;
  let sum = 0;
  dims.forEach((d, i) => {
    if (!d.enabled || d.score == null) return;
    const w = ws[i] ?? 1;
    wSum += w;
    sum += d.score * w;
  });
  return wSum > 0 ? Math.round(sum / wSum) : null;
};

/**
 * Phase 6 (model A): the per-student workspace gradebook. One grade card
 * per assigned resource — each worth `weight` points, graded directly
 * (rubric-default fill / override) out of that point value. The footer is
 * the straight points total + bonus. Shown while grading a contributor.
 */
export default function WorkspaceGradebookPanel() {
  const workspaceId = useAppSelector(
    (s: RootState) => s.workspace.workspace_id
  );
  const studentId = useAppSelector(selectGradingViewUserId);
  const username = useAppSelector(selectGradingViewUsername);
  const isGrading = useAppSelector(selectIsGradingViewActive);
  // Live rubric data for the resource currently open in the pane — used
  // to suggest a grade ("use rubric") on that one card.
  const readerGrade = useAppSelector(selectReadingGrade);
  const canvasGrade = useAppSelector(selectCanvasGrade);
  const dimWeights = useAppSelector(selectGradeDimensionWeights);
  const openReadingId = useAppSelector(selectSelectedReadingId);
  const openCanvasId = useAppSelector(selectSelectedCanvasId);
  const { showError, showSuccess } = useNotification();

  const [rows, setRows] = useState<Row[]>([]);
  const [override, setOverride] = useState<number | null>(null);
  const [overrideInput, setOverrideInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [releasingAll, setReleasingAll] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    if (!isGrading || !workspaceId || !studentId) return;
    let cancelled = false;
    setLoading(true);
    getStudentGradebook(workspaceId, studentId).then((res) => {
      if (cancelled) return;
      setLoading(false);
      if (!res.success) {
        showError(res.error.message || "Could not load the gradebook.");
        return;
      }
      setRows(res.data.resources);
      setOverride(res.data.override_total);
      setOverrideInput(
        res.data.override_total == null ? "" : String(res.data.override_total)
      );
    });
    return () => {
      cancelled = true;
    };
  }, [isGrading, workspaceId, studentId, showError]);

  const total = useMemo(
    () => computeWorkspaceTotal(rows, override),
    [rows, override]
  );

  // Live rubric auto-grade (0–100) for the open reading / canvas.
  const readerAuto = readerGrade.hasExpertReference
    ? weightedScore(
        [readerGrade.completeness, readerGrade.categoryAccuracy],
        [
          dimWeights.reader?.completeness ?? 1,
          dimWeights.reader?.categoryAccuracy ?? 1,
        ]
      )
    : null;
  const canvasAuto = canvasGrade.hasExpertReference
    ? weightedScore(
        [
          canvasGrade.nodeCoverage,
          canvasGrade.categoryAccuracy,
          canvasGrade.edgeCoverage,
        ],
        [
          dimWeights.canvas?.nodeCoverage ?? 1,
          dimWeights.canvas?.categoryAccuracy ?? 1,
          dimWeights.canvas?.edgeCoverage ?? 1,
        ]
      )
    : null;
  // The live rubric score for a card, if it's the resource open in the
  // pane (only that one has loaded data to score).
  const autoFor = (r: Row): number | null => {
    if (r.type === "reading" && r.resource_id === openReadingId)
      return readerAuto;
    if (r.type === "canvas" && r.resource_id === openCanvasId)
      return canvasAuto;
    return null;
  };

  const grouped = useMemo(() => {
    const m: Record<string, Row[]> = {};
    for (const r of rows) (m[r.type] ??= []).push(r);
    return m;
  }, [rows]);

  const patchRow = (id: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.resource_id === id ? { ...r, ...patch } : r)));

  // Persist the student's grade on a resource (points out of its point
  // value) + release state.
  const saveGrade = async (r: Row, releasedOverride?: boolean) => {
    if (!workspaceId || !studentId) return;
    const released = releasedOverride ?? r.released;
    patchRow(r.resource_id, { saving: true, released });
    const res = await setStudentGrade(
      r.type as "reading" | "canvas" | "paper",
      workspaceId,
      r.resource_id,
      studentId,
      { points: r.points, released }
    );
    patchRow(r.resource_id, { saving: false });
    if (!res.success) showError(res.error.message || "Could not save grade.");
  };

  const saveWeight = async (r: Row) => {
    if (!workspaceId) return;
    const res = await setResourceWeight(
      workspaceId,
      r.type as "reading" | "canvas" | "paper",
      r.resource_id,
      r.weight
    );
    if (!res.success) showError(res.error.message || "Could not save points.");
  };

  const releaseAll = async () => {
    if (!workspaceId || !studentId) return;
    setReleasingAll(true);
    const res = await releaseAllForStudent(workspaceId, studentId);
    setReleasingAll(false);
    if (!res.success) {
      showError(res.error.message || "Could not release all.");
      return;
    }
    // Reflect locally: every graded resource is now released.
    setRows((rs) =>
      rs.map((r) => (r.points != null ? { ...r, released: true } : r))
    );
    showSuccess("Released all grades for this student.");
  };

  const persistOverride = async (val: number | null) => {
    if (!workspaceId || !studentId) return;
    const res = await setStudentOverride(workspaceId, studentId, val);
    if (!res.success)
      showError(res.error.message || "Could not save the override.");
  };
  // Toggle the total between computed and a manual override — seeded from
  // the computed value, then editable in place.
  const toggleOverride = (checked: boolean) => {
    if (checked) {
      const start = total.earned;
      setOverride(start);
      setOverrideInput(String(start));
      persistOverride(start);
    } else {
      setOverride(null);
      setOverrideInput("");
      persistOverride(null);
    }
  };
  const onOverrideChange = (v: string) => {
    setOverrideInput(v);
    const n = v.trim() === "" ? 0 : Math.floor(Number(v));
    if (!Number.isNaN(n)) setOverride(n);
  };

  if (!isGrading || !studentId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Pick a contributor in the grading bar to grade their work.
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <Typography variant="subtitle1" component="div" sx={{ fontWeight: 600 }}>
          Workspace grade{username ? ` — ${username}` : ""}
        </Typography>
        <FormControlLabel
          sx={{ mr: 0 }}
          labelPlacement="start"
          control={
            <Switch
              size="small"
              checked={total.overridden}
              onChange={(e) => toggleOverride(e.target.checked)}
            />
          }
          label={<Typography variant="caption">Override</Typography>}
        />
      </Box>
      {/* The total grade — editable in place when override is on. Not a
          heading: it's a value, so it's a span. */}
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mb: 0.5 }}>
        {total.overridden ? (
          <TextField
            size="small"
            value={overrideInput}
            onChange={(e) => onOverrideChange(e.target.value)}
            onBlur={() => persistOverride(override)}
            inputProps={{
              inputMode: "numeric",
              "aria-label": "override total grade",
              style: {
                width: 56,
                fontSize: "1.4rem",
                fontWeight: 700,
                textAlign: "right",
                padding: "2px 6px",
              },
            }}
          />
        ) : (
          <Typography component="span" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
            {total.earned}
          </Typography>
        )}
        <Typography component="span" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
          /{total.outOf}
        </Typography>
        {total.percent != null && (
          <Typography component="span" variant="body2" color="text.secondary">
            ({total.percent}%)
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        component="div"
        sx={{ mb: 1.5 }}
      >
        {total.overridden
          ? `Overriding — computed ${total.earned} from ${total.gradedCount} graded.`
          : `${total.earned} from ${total.gradedCount} graded resource${
              total.gradedCount === 1 ? "" : "s"
            }, of ${total.possible} possible.`}
      </Typography>

      {loading ? (
        <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
          <CircularProgress size={20} />
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No resources in this workspace yet.
        </Typography>
      ) : (
        <>
          {TYPE_ORDER.filter((t) => grouped[t]?.length).map((t) => (
            <Box key={t} sx={{ mb: 1.5 }}>
              <Typography
                variant="subtitle2"
                component="div"
                sx={{ mb: 0.5 }}
              >
                {TYPE_LABEL[t]} ({grouped[t]?.length})
              </Typography>
              {(grouped[t] ?? []).map((r) => (
                <Box
                  key={r.resource_id}
                  sx={(th) => ({
                    p: 1.5,
                    mb: 1.5,
                    border: `1px solid ${th.palette.divider}`,
                    borderRadius: 1,
                  })}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1.25,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        mr: 1,
                      }}
                    >
                      {r.name || "Untitled"}
                    </Typography>
                    {r.released && (
                      <Chip label="released" size="small" variant="outlined" />
                    )}
                  </Box>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <TextField
                      label="Grade"
                      type="number"
                      size="small"
                      value={r.points == null ? "" : r.points}
                      onChange={(e) =>
                        patchRow(r.resource_id, {
                          points:
                            e.target.value === ""
                              ? null
                              : clampInt(e.target.value),
                        })
                      }
                      onBlur={() => saveGrade(r)}
                      disabled={r.saving}
                      InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                      inputProps={{
                        min: 0,
                        "aria-label": `grade for ${r.name || "resource"}`,
                        style: { width: 58 },
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      /
                    </Typography>
                    <TextField
                      label="Points"
                      type="number"
                      size="small"
                      value={r.weight}
                      onChange={(e) =>
                        patchRow(r.resource_id, {
                          weight: clampInt(e.target.value),
                        })
                      }
                      onBlur={() => saveWeight(r)}
                      InputLabelProps={{ sx: { fontSize: "0.8rem" } }}
                      inputProps={{
                        min: 0,
                        "aria-label": `point value for ${r.name || "resource"}`,
                        style: { width: 58 },
                      }}
                    />
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">
                      Release
                    </Typography>
                    <Switch
                      size="small"
                      checked={r.released}
                      onChange={(e) => saveGrade(r, e.target.checked)}
                      disabled={r.saving}
                      inputProps={{
                        "aria-label": `release ${r.name || "resource"}`,
                      }}
                    />
                  </Box>
                  {(() => {
                    const auto = autoFor(r);
                    if (auto == null) return null;
                    const suggested = Math.round((auto / 100) * r.weight);
                    const open = expandedId === r.resource_id;
                    return (
                      <Box sx={{ mt: 1.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.25,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Rubric {auto}% → {suggested}/{r.weight}
                          </Typography>
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() => {
                              patchRow(r.resource_id, { points: suggested });
                              saveGrade({ ...r, points: suggested });
                            }}
                            disabled={r.saving}
                          >
                            use
                          </Button>
                          <Box sx={{ flex: 1 }} />
                          <Button
                            size="small"
                            variant="outlined"
                            color="inherit"
                            onClick={() =>
                              setExpandedId(open ? null : r.resource_id)
                            }
                            endIcon={
                              open ? <ExpandLessIcon /> : <ExpandMoreIcon />
                            }
                            aria-expanded={open}
                          >
                            breakdown
                          </Button>
                        </Box>
                        <Collapse in={open} unmountOnExit>
                          <Box sx={{ mt: 1 }}>
                            <RubricBreakdown
                              kind={r.type as "reading" | "canvas"}
                              reader={r.type === "reading" ? readerGrade : null}
                              canvas={r.type === "canvas" ? canvasGrade : null}
                            />
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })()}
                </Box>
              ))}
            </Box>
          ))}

          <Divider sx={{ my: 1.5 }} />
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={releaseAll}
            disabled={releasingAll || total.gradedCount === 0}
          >
            {releasingAll ? (
              <CircularProgress size={16} />
            ) : (
              "Release all grades"
            )}
          </Button>
        </>
      )}
    </Box>
  );
}
